
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { UserRole, type Lead, type Listing, type TourSlot, type Application, type Lease, type LeaseRenewalTerms, type RenewalOfferTerms, type Tenant } from '@repo/shared-types';
import { useAppStore } from '../../store';
import { MarketingSiteSettings } from '@repo/shared-types/settings';
import { useToast } from '../../components/common/ToastContext';
import { applicationSchema, leaseSchema, listingSchema, leadSchema, tourSlotSchema, renewalOfferSchema, backgroundCheckSchema, tenantSchema } from '@repo/shared-types/leasing/schemas';
import { entityNoteListItemSchema } from '@repo/shared-types/common/noteSchemas';
import type { Note, NoteCategory } from '@repo/shared-types/common/notes';
import { logger, validateEntity, validateEntityArray, validateSuccessResponse } from '@repo/shared-utils';

/** Leasing-scoped resident directory for PM lease UI (GET /leasing/tenants). */
export const useLeaseResidents = (filters?: Record<string, string>) => {
  return useQuery({
    queryKey: ['leasing', 'residents', filters ?? {}],
    queryFn: async () => {
      const raw = await api.leasing.residents.list(filters);
      const list = Array.isArray(raw) ? raw : (raw as { data: Tenant[] }).data;
      const validation = validateEntityArray(tenantSchema, list);
      if (!validation.valid) {
        logger.error('Invalid lease residents query response:', validation.errors);
        throw new Error('Invalid resident data received from API');
      }
      return validation.data! as Tenant[];
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// LEADS
export const useLeads = () => {
  return useQuery({
    queryKey: ['leasing', 'leads'],
    queryFn: async () => {
      const data = await api.leasing.leads.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(leadSchema, data);
      if (!validation.valid) {
        logger.error('Invalid leads query response:', validation.errors);
        throw new Error('Invalid leads data received from API');
      }
      return validation.data! as Lead[];
    },
  });
};

export const useCreateLead = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (lead: Lead) => api.leasing.leads.create(lead),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leasing', 'leads'] }),
        queryClient.invalidateQueries({ queryKey: ['leasing', 'tours'] }),
      ]);
      showToast('Lead captured successfully', 'success');
    }
  });
};

export const useUpdateLead = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Lead> }) =>
      api.leasing.leads.update(id, updates),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leasing', 'leads'] }),
        queryClient.invalidateQueries({ queryKey: ['leasing', 'tours'] }),
      ]);
      showToast('Lead updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update lead', 'error');
    }
  });
};

export const useDeleteLead = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.leasing.leads.delete(id),
    onSuccess: async (data) => {
      // ✅ Validate success response
      const validation = validateSuccessResponse(data);
      if (!validation.valid) {
        logger.error('Invalid delete response from API:', validation.errors);
        showToast('Lead archived but received invalid response from server', 'error');
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['leasing', 'leads'] }),
          queryClient.invalidateQueries({ queryKey: ['leasing', 'tours'] }),
        ]);
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leasing', 'leads'] }),
        queryClient.invalidateQueries({ queryKey: ['leasing', 'tours'] }),
      ]);
      showToast('Lead archived successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to archive lead', 'error');
    }
  });
};

// APPLICATIONS
export const useApplications = (filters?: Record<string, string>) => {
  return useQuery({
    queryKey: ['leasing', 'applications', filters ?? {}],
    queryFn: async () => {
      const data = await api.leasing.applications.list(filters);
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(applicationSchema, data);
      if (!validation.valid) {
        const errorCount = validation.errors?.length || 0;
        const errorDetails = validation.errors?.slice(0, 3).map(({ index, error }) => {
          const app = data[index];
          const fieldErrors = error.errors.map(e => `${e.path.join('.')}: ${e.message}`).join('; ');
          return `Record ${index} (ID: ${app?.id || 'unknown'}): ${fieldErrors}`;
        }).join(' | ') || 'Unknown validation errors';

        logger.warn('Applications query response did not match schema; returning empty list', {
          queryKey: 'leasing.applications',
          totalRecords: Array.isArray(data) ? data.length : 0,
          invalidRecords: errorCount,
          errorDetails: errorCount > 3 ? `${errorDetails}... (${errorCount - 3} more)` : errorDetails,
        });
        return [] as unknown as Application[];
      }
      return validation.data! as unknown as Application[];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes - applications change frequently
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });
};

export const useCreateApplication = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (application: Application) => api.leasing.applications.create(application),
    onMutate: async (newApplication) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['leasing', 'applications'] });

      // Snapshot the previous value
      const previousApplications = queryClient.getQueryData<Application[]>(['leasing', 'applications']);

      // Optimistically update to the new value
      queryClient.setQueryData<Application[]>(['leasing', 'applications'], (old = []) => [
        newApplication,
        ...old,
      ]);

      // Return a context object with the snapshotted value
      return { previousApplications };
    },
    onError: (err, newApplication, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousApplications) {
        queryClient.setQueryData(['leasing', 'applications'], context.previousApplications);
      }
      showToast('Failed to create application', 'error');
    },
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(applicationSchema, data);
      if (!validation.valid) {
        logger.error('Invalid application response from API:', validation.errors);
        showToast('Application created but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['leasing', 'applications'] });
        return;
      }
      showToast('Application created successfully', 'success');
    },
    onSettled: async () => {
      // Always refetch after error or success to ensure consistency
      await queryClient.invalidateQueries({ queryKey: ['leasing', 'applications'] });
    },
  });
};

export const useUpdateApplication = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (payload: { id: string; status?: Application['status']; updates?: Partial<Application> }) => {
      const updates = payload.updates ?? (payload.status === undefined ? {} : { status: payload.status });
      return api.leasing.applications.update(payload.id, updates);
    },
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(applicationSchema, data);
      if (!validation.valid) {
        logger.error('Invalid application response from API:', validation.errors);
        showToast('Application updated but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['leasing', 'applications'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['leasing', 'applications'] });
      showToast('Application updated', 'success');
    }
  });
};

export const useDeleteApplication = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.leasing.applications.delete(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leasing', 'applications'] });
      showToast('Personal information removed from this application', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Could not remove application data', 'error');
    }
  });
};

// APPLICATION NOTES
export const useApplicationNotes = (applicationId: string) => {
  return useQuery({
    queryKey: ['leasing', 'applications', 'notes', applicationId],
    queryFn: async () => {
      const data = await api.leasing.applications.notes.list(applicationId);
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(entityNoteListItemSchema, data);
      if (!validation.valid) {
        logger.error('Invalid application notes query response:', validation.errors);
        throw new Error('Invalid application notes data received from API');
      }
      return validation.data! as Note[];
    },
    enabled: !!applicationId,
  });
};

export const useCreateApplicationNote = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (note: { applicationId: string; content: string; category?: string; isImportant?: boolean }) =>
      api.leasing.applications.notes.create(note),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leasing', 'applications', 'notes', variables.applicationId] }),
        queryClient.invalidateQueries({ queryKey: ['notes', 'all'] }),
      ]);
      showToast('Note added successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create note', 'error');
    }
  });
};

export const useUpdateApplicationNote = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates, _applicationId }: { id: string; updates: { content?: string; category?: NoteCategory; isImportant?: boolean }; _applicationId: string }) =>
      api.leasing.applications.notes.update(id, updates),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leasing', 'applications', 'notes', variables._applicationId] }),
        queryClient.invalidateQueries({ queryKey: ['notes', 'all'] }),
      ]);
      showToast('Note updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update note', 'error');
    }
  });
};

export const useDeleteApplicationNote = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, _applicationId }: { id: string; _applicationId: string }) =>
      api.leasing.applications.notes.delete(id),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leasing', 'applications', 'notes', variables._applicationId] }),
        queryClient.invalidateQueries({ queryKey: ['notes', 'all'] }),
      ]);
      showToast('Note deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete note', 'error');
    }
  });
};

// LISTINGS
export const useListings = () => {
  return useQuery({
    queryKey: ['leasing', 'listings'],
    queryFn: async () => {
      const data = await api.leasing.listings.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(listingSchema, data);
      if (!validation.valid) {
        logger.error('Invalid listings query response:', validation.errors);
        throw new Error('Invalid listings data received from API');
      }
      return validation.data! as Listing[];
    },
  });
};

export const useCreateListing = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (listing: Listing) => api.leasing.listings.create(listing),
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(listingSchema, data);
      if (!validation.valid) {
        logger.error('Invalid listing response from API:', validation.errors);
        showToast('Listing created but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['leasing', 'listings'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['leasing', 'listings'] });
      showToast('Listing created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create listing', 'error');
    }
  });
};

export const useUpdateListing = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Listing> }) => api.leasing.listings.update(id, updates),
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(listingSchema, data);
      if (!validation.valid) {
        logger.error('Invalid listing response from API:', validation.errors);
        showToast('Listing updated but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['leasing', 'listings'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['leasing', 'listings'] });
      showToast('Listing updated', 'success');
    }
  });
};

export const useDeleteListing = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => api.leasing.listings.delete(id),
    onSuccess: async (data) => {
      // ✅ Validate success response
      const validation = validateSuccessResponse(data);
      if (!validation.valid) {
        logger.error('Invalid delete response from API:', validation.errors);
        showToast('Listing deleted but received invalid response from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['leasing', 'listings'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['leasing', 'listings'] });
      showToast('Listing deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete listing', 'error');
    }
  });
};

// TOURS
export const useTourSlots = () => {
  return useQuery({
    queryKey: ['leasing', 'tours'],
    queryFn: async () => {
      const data = await api.leasing.tours.list();
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(tourSlotSchema, data);
      if (!validation.valid) {
        logger.error('Invalid tour slots query response:', validation.errors);
        throw new Error('Invalid tour slots data received from API');
      }
      return validation.data! as TourSlot[];
    },
  });
};

export const useCreateTourSlot = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (tourSlot: Omit<TourSlot, 'id'>) => api.leasing.tours.create(tourSlot),
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(tourSlotSchema, data);
      if (!validation.valid) {
        logger.error('Invalid tour slot response from API:', validation.errors);
        showToast('Tour scheduled but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['leasing', 'tours'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['leasing', 'tours'] });
      showToast('Tour scheduled successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to schedule tour', 'error');
    }
  });
};

export const useAssignLeadsToTourSlot = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ slotId, leadIds }: { slotId: string; leadIds: string[] }) =>
      api.leasing.tours.assignLeads(slotId, leadIds),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leasing', 'tours'] }),
        queryClient.invalidateQueries({ queryKey: ['leasing', 'leads'] }),
      ]);
      showToast('Leads assigned to tour successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to assign leads to tour', 'error');
    },
  });
};

// LEASES
export const useLeases = (filters?: Record<string, string>) => {
  return useQuery({
    queryKey: ['leasing', 'leases', filters ?? {}],
    queryFn: async () => {
      const raw = await api.leasing.leases.list(filters);
      const list = Array.isArray(raw) ? raw : (raw as { data: Lease[] }).data;
      const validation = validateEntityArray(leaseSchema, list);
      if (!validation.valid) {
        logger.error('Invalid leases query response:', validation.errors);
        throw new Error('Invalid leases data received from API');
      }
      return validation.data! as Lease[];
    },
  });
};

export const useLeasesPaginated = (filters: Record<string, string>) => {
  return useQuery({
    queryKey: ['leasing', 'leases', 'paginated', filters],
    queryFn: async () => {
      const raw = await api.leasing.leases.list(filters);
      const list = Array.isArray(raw) ? raw : (raw as { data: Lease[] }).data;
      const total = Array.isArray(raw) ? raw.length : (raw as { data: Lease[]; total: number }).total;
      const validation = validateEntityArray(leaseSchema, list);
      if (!validation.valid) {
        logger.error('Invalid leases query response:', validation.errors);
        throw new Error('Invalid leases data received from API');
      }
      return { data: validation.data! as Lease[], total };
    },
  });
};

export const useLease = (id: string | undefined) => {
  return useQuery({
    queryKey: ['leasing', 'leases', id],
    queryFn: async () => {
      const data = await api.leasing.leases.get(id!);
      // ✅ Validate query response with Zod schema
      if (data) {
        const validation = validateEntity(leaseSchema, data);
        if (!validation.valid) {
          logger.error('Invalid lease query response:', validation.errors);
          throw new Error('Invalid lease data received from API');
        }
        return validation.data! as Lease;
      }
      return null;
    },
    enabled: !!id,
  });
};

export const useTenantPortalLeaseJourney = () => {
  const userRole = useAppStore((s) => s.userRole);
  const isTenant = userRole === UserRole.TENANT;
  return useQuery({
    queryKey: ['leasing', 'leases', 'portal-journey'],
    queryFn: () => api.leasing.leases.getPortalJourney(),
    staleTime: 60_000,
    enabled: isTenant,
  });
};

export const useLeaseByApplication = (applicationId: string | undefined) => {
  return useQuery({
    queryKey: ['leasing', 'leases', 'byApplication', applicationId],
    queryFn: async () => {
      const list = await api.leasing.leases.getByApplication(applicationId!);
      const validation = validateEntityArray(leaseSchema, list);
      if (!validation.valid) {
        logger.error('Invalid leases-by-application query response:', validation.errors);
        throw new Error('Invalid lease data received from API');
      }
      const rows = validation.data ?? [];
      // API returns newest first; UI flows expect a single primary lease.
      return (rows.length === 0 ? null : rows[0]) as Lease | null;
    },
    enabled: !!applicationId,
  });
};

export const useCreateLease = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (lease: Omit<Lease, 'id' | 'createdAt' | 'updatedAt'>) => api.leasing.leases.create(lease),
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(leaseSchema, data);
      if (!validation.valid) {
        logger.error('Invalid lease response from API:', validation.errors);
        showToast('Lease created but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['leasing', 'leases'] });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['leasing', 'leases'] });
      showToast('Lease created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create lease', 'error');
    }
  });
};

export const useUpdateLease = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Lease> }) => api.leasing.leases.update(id, updates),
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(leaseSchema, data);
      if (!validation.valid) {
        logger.error('Invalid lease response from API:', validation.errors);
        showToast('Lease updated but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['leasing', 'leases'] });
        return;
      }
      const validatedLease = validation.data! as Lease;
      const invalidations: Promise<unknown>[] = [
        queryClient.invalidateQueries({ queryKey: ['leasing', 'leases'] }),
      ];
      if (validatedLease.id) {
        invalidations.push(queryClient.invalidateQueries({ queryKey: ['leasing', 'leases', validatedLease.id] }));
      }
      await Promise.all(invalidations);
      showToast('Lease updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update lease', 'error');
    }
  });
};

export const useGenerateLeaseFromApplication = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (applicationId: string) => api.leasing.leases.generateFromApplication(applicationId),
    onSuccess: async (lease) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(leaseSchema, lease);
      if (!validation.valid) {
        logger.error('Invalid lease response from API:', validation.errors);
        showToast('Lease generated but received invalid data from server', 'error');
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['leasing', 'leases'] }),
          queryClient.invalidateQueries({ queryKey: ['leasing', 'applications'] }),
        ]);
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leasing', 'leases'] }),
        queryClient.invalidateQueries({ queryKey: ['leasing', 'applications'] }),
      ]);
      showToast('Lease generated successfully', 'success');
      return validation.data!;
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to generate lease', 'error');
    }
  });
};

export const useSendLeaseForSignature = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ leaseId, tenantEmail }: { leaseId: string; tenantEmail: string }) =>
      api.leasing.leases.sendForSignature(leaseId, tenantEmail),
    onSuccess: async (lease) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(leaseSchema, lease);
      if (!validation.valid) {
        logger.error('Invalid lease response from API:', validation.errors);
        showToast('Lease sent but received invalid data from server', 'error');
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['leasing', 'leases'] }),
          queryClient.invalidateQueries({ queryKey: ['leasing', 'applications'] }),
        ]);
        return;
      }
      const validatedLease = validation.data! as Lease;
      const invalidations: Promise<unknown>[] = [
        queryClient.invalidateQueries({ queryKey: ['leasing', 'leases'] }),
        queryClient.invalidateQueries({ queryKey: ['leasing', 'applications'] }),
      ];
      // Start polling for envelope status if envelope ID exists
      if (validatedLease.esignatureEnvelopeId) {
        invalidations.push(
          queryClient.invalidateQueries({ queryKey: ['docusign', 'envelope', validatedLease.esignatureEnvelopeId] }),
        );
      }
      await Promise.all(invalidations);
      showToast('Lease sent for signature successfully', 'success');
      return validatedLease;
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to send lease for signature', 'error');
    }
  });
};

export const useEnvelopeStatus = (envelopeId: string | undefined, enabled: boolean = true) => {
  const { data: isDocuSignConfigured } = useQuery({
    queryKey: ['docusign', 'configured'],
    queryFn: () => api.leasing.docusign.isConfigured(),
  });
  return useQuery({
    queryKey: ['docusign', 'envelope', envelopeId],
    queryFn: async () => {
      if (!envelopeId) throw new Error('Envelope ID required');
      return api.leasing.docusign.getEnvelopeStatus(envelopeId);
    },
    enabled: enabled && !!envelopeId && isDocuSignConfigured === true,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Stop polling if signed, completed, declined, or voided
      if (data && (data.status === 'signed' || data.status === 'completed' || data.status === 'declined' || data.status === 'voided')) {
        return false;
      }
      // Poll every 10 seconds while waiting
      return 10000;
    },
    refetchIntervalInBackground: true
  });
};

export const useRenewalOffers = (leaseId?: string) => {
  return useQuery({
    queryKey: ['leasing', 'renewals', leaseId],
    queryFn: async () => {
      const data = await api.leasing.leases.renewals.list(leaseId);
      // ✅ Validate query response with Zod schema
      const validation = validateEntityArray(renewalOfferSchema, data);
      if (!validation.valid) {
        logger.error('Invalid renewal offers query response:', validation.errors);
        throw new Error('Invalid renewal offers data received from API');
      }
      return validation.data! as import('@repo/shared-types/leasing/schemas').RenewalOffer[];
    },
    enabled: !!leaseId
  });
};

export const useCreateRenewalOffer = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ leaseId, offerData }: { leaseId: string; offerData: RenewalOfferTerms }) =>
      api.leasing.leases.renewals.create(leaseId, offerData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leasing', 'renewals'] });
      showToast('Renewal offer created successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to create renewal offer', 'error');
    }
  });
};

export const useSendRenewal = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (offerId: string) => api.leasing.leases.renewals.send(offerId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['leasing', 'renewals'] });
      showToast('Renewal offer sent successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to send renewal offer', 'error');
    }
  });
};

// MARKETING SITE SETTINGS
export const useMarketingSiteSettings = () => {
  return useQuery({
    queryKey: ['marketing', 'site', 'settings'],
    queryFn: () => api.marketing.site.get(),
  });
};

export const useUpdateMarketingSiteSettings = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (updates: Partial<MarketingSiteSettings>) =>
      api.marketing.site.update(updates),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['marketing', 'site', 'settings'] });
      showToast('Site settings updated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to update site settings', 'error');
    }
  });
};

// BACKGROUND CHECK
export const useRequestBackgroundCheck = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ applicationId, provider }: { applicationId: string; provider?: 'checkr' | 'transunion' }) =>
      api.leasing.applications.requestBackgroundCheck(applicationId, provider || 'checkr'),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leasing', 'applications'] }),
        queryClient.invalidateQueries({ queryKey: ['leasing', 'applications', variables.applicationId] }),
      ]);
      showToast('Background check requested successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to request background check', 'error');
    }
  });
};

export const useBackgroundCheckStatus = (requestId?: string) => {
  return useQuery({
    queryKey: ['leasing', 'applications', 'backgroundCheck', requestId],
    queryFn: async () => {
      const data = await api.leasing.applications.getBackgroundCheckStatus(requestId!);
      // ✅ Validate query response with Zod schema
      if (data) {
        const validation = validateEntity(backgroundCheckSchema, data);
        if (!validation.valid) {
          logger.error('Invalid background check status query response:', validation.errors);
          throw new Error('Invalid background check status data received from API');
        }
        return validation.data! as import('@repo/shared-types/leasing/schemas').BackgroundCheck;
      }
      return null;
    },
    enabled: !!requestId,
  });
};

export const useSignLease = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ leaseId, signatureData }: { leaseId: string; signatureData: { type: 'tenant' | 'landlord'; signature: string; signedBy?: string } }) =>
      api.leasing.leases.sign(leaseId, signatureData),
    onSuccess: async (lease) => {
      // ✅ Validate API response with Zod schema
      const validation = validateEntity(leaseSchema, lease);
      if (!validation.valid) {
        logger.error('Invalid lease response from API:', validation.errors);
        showToast('Lease signed but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['leasing', 'leases'] });
        return;
      }
      const validatedLease = validation.data! as Lease;
      const invalidations: Promise<unknown>[] = [
        queryClient.invalidateQueries({ queryKey: ['leasing', 'leases'] }),
      ];
      if (validatedLease.id) {
        invalidations.push(queryClient.invalidateQueries({ queryKey: ['leasing', 'leases', validatedLease.id] }));
      }
      await Promise.all(invalidations);
      showToast('Lease signed successfully', 'success');
      return validatedLease;
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to sign lease', 'error');
    }
  });
};

export const useActivateLease = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (leaseId: string) => api.leasing.leases.activate(leaseId),
    onSuccess: async (lease) => {
      const validation = validateEntity(leaseSchema, lease);
      if (!validation.valid) {
        logger.error('Invalid lease response from activate:', validation.errors);
        showToast('Lease activated but received invalid data from server', 'error');
        await queryClient.invalidateQueries({ queryKey: ['leasing', 'leases'] });
        return;
      }
      const invalidations: Promise<unknown>[] = [
        queryClient.invalidateQueries({ queryKey: ['leasing', 'leases'] }),
      ];
      if (lease.id) {
        invalidations.push(queryClient.invalidateQueries({ queryKey: ['leasing', 'leases', lease.id] }));
      }
      await Promise.all(invalidations);
      showToast('Lease activated', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to activate lease', 'error');
    },
  });
};

export const useRenewLease = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({ leaseId, newTerms }: { leaseId: string; newTerms: LeaseRenewalTerms }) =>
      api.leasing.leases.renew(leaseId, newTerms),
    onSuccess: async (newLease) => {
      await queryClient.invalidateQueries({ queryKey: ['leasing', 'leases'] });
      if (newLease?.id) {
        await queryClient.invalidateQueries({ queryKey: ['leasing', 'leases', newLease.id] });
      }
      showToast('Renewal draft created', 'success');
      return newLease;
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to renew lease', 'error');
    }
  });
};

export const useTerminateLease = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({
      leaseId,
      noticeDate,
      terminationDate,
      reason,
      notes,
    }: {
      leaseId: string;
      noticeDate: string;
      terminationDate: string;
      reason: string;
      notes?: string;
    }) =>
      api.leasing.leases.terminate(leaseId, {
        noticeDate,
        terminationDate,
        reason,
        ...(notes ? { notes } : {}),
      }),
    onSuccess: async (data) => {
      // ✅ Validate API response with Zod schema (if lease is returned)
      if (data) {
        const validation = validateEntity(leaseSchema, data);
        if (!validation.valid) {
          logger.error('Invalid lease response from API:', validation.errors);
          showToast('Lease terminated but received invalid data from server', 'error');
          await queryClient.invalidateQueries({ queryKey: ['leasing', 'leases'] });
          return;
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['leasing', 'leases'] });
      showToast('Lease terminated successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to terminate lease', 'error');
    }
  });
};


